"use client";
import { useAppContext } from "@/providers/AppProvider";
import { ICertificate, ISkill } from "@/types";
import { format } from "date-fns";
import { KeyboardEvent, useEffect, useRef, useState } from "react";

type Message = {
    text: string;
    sender: "user" | "system";
    isCommand?: boolean;
};

export default function Terminal() {
    const {
        user,
        educations,
        experiences,
        skills,
        projects,
        achievements,
        certificates,
    } = useAppContext();
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState<Message[]>([
        {
            text: `Welcome to ${user?.name}'s interactive terminal\nType 'help' to begin`,
            sender: "system",
        },
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const [commandHistory, setCommandHistory] = useState<string[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Scroll to bottom of messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Tab") {
            e.preventDefault();
            handleTabAutocomplete(input);
        } else if (e.key === "ArrowUp" && commandHistory.length > 0) {
            e.preventDefault();
            const newIndex = Math.min(
                historyIndex + 1,
                commandHistory.length - 1,
            );
            setHistoryIndex(newIndex);
            setInput(
                commandHistory[commandHistory.length - 1 - newIndex] || "",
            );
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            const newIndex = Math.max(historyIndex - 1, -1);
            setHistoryIndex(newIndex);
            setInput(
                newIndex === -1
                    ? ""
                    : commandHistory[commandHistory.length - 1 - newIndex] ||
                          "",
            );
        }
    };

    const handleTabAutocomplete = (currentInput: string) => {
        const trimmed = currentInput.toLowerCase();
        if (!trimmed) return;

        if (trimmed.startsWith("cat ")) {
            const filePrefix = trimmed.slice(4);
            const files = [
                "about.txt",
                "achievements.txt",
                "certificates.txt",
                "contact.txt",
                "education.txt",
                "experience.txt",
                "projects.txt",
                "skills.txt",
            ];
            const matches = files.filter((f) => f.startsWith(filePrefix));
            if (matches.length === 1) {
                setInput(`cat ${matches[0]}`);
            } else if (matches.length > 1) {
                setMessages((prev) => [
                    ...prev,
                    { text: currentInput, sender: "user", isCommand: true },
                    { text: matches.join("   "), sender: "system" },
                ]);
            }
            return;
        }

        const commands = [
            "about",
            "education",
            "experience",
            "skills",
            "projects",
            "certificates",
            "achievements",
            "contact",
            "socials",
            "ls",
            "cat",
            "stats",
            "whoami",
            "quote",
            "date",
            "sudo",
            "clear",
            "help",
            "matrix",
        ];

        const matches = commands.filter((cmd) => cmd.startsWith(trimmed));

        if (matches.length === 1) {
            setInput(matches[0]);
        } else if (matches.length > 1) {
            setMessages((prev) => [
                ...prev,
                { text: currentInput, sender: "user", isCommand: true },
                { text: matches.join("   "), sender: "system" },
            ]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim()) return;

        setMessages((prev) => [
            ...prev,
            { text: input, sender: "user", isCommand: true },
        ]);
        setCommandHistory((prev) => [...prev, input]);
        setInput("");
        setHistoryIndex(-1);
        setIsLoading(true);

        setTimeout(() => {
            const response = generateResponse(input);
            setMessages((prev) => [
                ...prev,
                {
                    text: response || "Command not recognized",
                    sender: "system",
                },
            ]);
            setIsLoading(false);
        }, 200);
    };

    const formatCardResponse = <T,>(
        title: string,
        items: T[],
        mapper: (item: T, index: number) => string,
        prefix?: string,
    ) => {
        const border = "─".repeat(25);
        return `${prefix || ""}\n🌟 ${title.toUpperCase()} 🌟\n${border}\n${items
            .map((item, idx) => `#${idx + 1}\n${mapper(item, idx)}\n${border}`)
            .join("\n")}`;
    };

    const generateResponse = (command: string) => {
        const cmd = command.toLowerCase().trim();

        if (cmd === "clear") {
            setMessages([]);
            return " ";
        } else if (cmd === "hi" || cmd.includes("hello")) {
            return `👋 Hello! I'm ${user?.name}.\n\nType 'help' to see available commands.`;
        } else if (cmd.includes("bio") || cmd.includes("about")) {
            return `📖 About Me:\n${user?.about || "No bio available"}`;
        } else if (cmd.includes("education") || cmd.includes("study")) {
            return formatCardResponse(
                "Education",
                educations,
                (edu) =>
                    `🎓 ${edu.degree}\n🏫 ${edu.institution}\n📅 ${format(edu.startDate, "dd/MM/yyyy")} - ${edu.endDate ? format(edu.endDate, "dd/MM/yyyy") : "Present"}`,
            );
        } else if (cmd.includes("experience") || cmd.includes("work")) {
            return formatCardResponse(
                "Experience",
                experiences,
                (exp) =>
                    `💼 ${exp.role} @ ${exp.company}\n📅 ${format(exp.startDate, "dd/MM/yyyy")} - ${exp.endDate ? format(exp.endDate, "dd/MM/yyyy") : "Present"}\n${exp.descriptions.map((d: string) => `• ${d}`).join("\n")}`,
            );
        } else if (cmd.includes("skill") || cmd.includes("tech")) {
            return formatCardResponse(
                "Skills",
                skills,
                (skill) =>
                    `📁 ${skill.name}\n${skill.skills.map((s: ISkill) => `- ${s.name}`).join("\n")}`,
            );
        } else if (cmd.includes("project")) {
            return formatCardResponse(
                "Projects",
                projects
                    .sort((a, b) => {
                        if (a.createdAt === undefined) return -1; // a comes first
                        if (b.createdAt === undefined) return 1; // b comes first
                        return (
                            new Date(b.createdAt).getTime() -
                            new Date(a.createdAt).getTime()
                        );
                    })
                    .slice(0, 3),
                (project) =>
                    `📂 ${project.name}\n📝 ${project.description}\n🛠 Technologies: ${project.skills.join(", ")}`,
                "Top 3 projects:\n",
            );
        } else if (cmd.includes("achievement")) {
            return formatCardResponse(
                "Achievements",
                achievements,
                (ach) =>
                    `🏅 ${ach.title}\n🏢 ${ach.company} • ${format(ach.date, "dd/MM/yyyy")}\n📝 ${ach.description}`,
            );
        } else if (cmd.includes("certif")) {
            return formatCardResponse(
                "Certificates",
                certificates,
                (cert: ICertificate) => `📜 ${cert.title}`,
            );
        } else if (
            cmd === "contact" ||
            cmd === "social" ||
            cmd === "socials" ||
            cmd === "email"
        ) {
            const links = user?.socialLinks;
            return `📬 CONTACT & SOCIALS\n─────────────────────────\n📧 Email: ${user?.email || "N/A"}\n\n🌐 Social Links:\n${links?.github ? `• GitHub: ${links.github}\n` : ""}${links?.linkedin ? `• LinkedIn: ${links.linkedin}\n` : ""}${links?.twitter ? `• Twitter: ${links.twitter}\n` : ""}${links?.instagram ? `• Instagram: ${links.instagram}\n` : ""}${links?.stackOverflow ? `• StackOverflow: ${links.stackOverflow}` : ""}`;
        } else if (cmd === "ls" || cmd === "dir") {
            return `📂 Directory Listing:\n\n📄 about.txt\n📄 experience.txt\n📄 education.txt\n📄 skills.txt\n📄 projects.txt\n📄 certificates.txt\n📄 achievements.txt\n📄 contact.txt\n\n💡 Tip: Use 'cat <filename>' (e.g., 'cat experience.txt') to view files!`;
        } else if (cmd.startsWith("cat ")) {
            const target = cmd.replace("cat ", "").trim();
            if (target === "about.txt" || target === "about")
                return generateResponse("about");
            if (
                target === "experience.txt" ||
                target === "experience" ||
                target === "work"
            )
                return generateResponse("experience");
            if (
                target === "education.txt" ||
                target === "education" ||
                target === "study"
            )
                return generateResponse("education");
            if (
                target === "skills.txt" ||
                target === "skills" ||
                target === "tech"
            )
                return generateResponse("skills");
            if (target === "projects.txt" || target === "projects")
                return generateResponse("projects");
            if (target === "contact.txt" || target === "contact")
                return generateResponse("contact");
            if (target === "certificates.txt" || target === "certificates")
                return generateResponse("certif");
            if (target === "achievements.txt" || target === "achievements")
                return generateResponse("achievement");
            return `cat: ${target}: No such file or directory. Try 'ls' to see available files.`;
        } else if (cmd.startsWith("sudo")) {
            return `⛔ Permission denied: You are not in the sudoers file. This incident will be reported to ${user?.name}! 😉`;
        } else if (cmd === "whoami") {
            return `👤 You are a valued visitor exploring ${user?.name}'s portfolio! Welcome!`;
        } else if (cmd === "date" || cmd === "time") {
            return `📅 ${new Date().toLocaleString()}`;
        } else if (cmd === "quote") {
            const quotes = [
                '"First, solve the problem. Then, write the code." – John Johnson',
                '"Code is like humor. When you have to explain it, it’s bad." – Cory House',
                '"Simplicity is prerequisite for reliability." – Edsger W. Dijkstra',
                '"Make it work, make it right, make it fast." – Kent Beck',
            ];
            return quotes[Math.floor(Math.random() * quotes.length)];
        } else if (cmd === "stats" || cmd === "github") {
            return `📊 GITHUB & CODE STATS\n─────────────────────────\n⭐ Projects: ${projects.length}\n🎓 Educations: ${educations.length}\n💼 Experiences: ${experiences.length}\n🏅 Achievements: ${achievements.length}\n📜 Certificates: ${certificates.length}`;
        } else if (cmd === "matrix") {
            return `💚 Wake up, Neo...\nThe Matrix has you.\nFollow the white rabbit. 🐇`;
        } else if (cmd.includes("help")) {
            return `📖 Available commands:\n\n- about        → Personal bio\n- education    → Academic background\n- experience   → Work history\n- skills       → Technical abilities\n- projects     → My work & top projects\n- certificates → Earned certificates\n- achievements → Notable accomplishments\n- contact      → Email & socials\n- ls           → List virtual directory files\n- cat <file>   → Read virtual file content\n- stats        → Portfolio quick summary\n- whoami       → Current visitor info\n- quote        → Random developer quote\n- date         → Current date and time\n- sudo         → Admin superuser access test\n- clear        → Reset terminal\n- help         → Show this menu`;
        } else {
            return `❌ Command not found. Type 'help' to see available commands.`;
        }
    };

    return (
        <div className="flex h-full max-h-[90vh] w-full flex-col p-2 font-mono md:max-h-screen">
            <div className="mb-4">
                <h1 className="text-xl font-semibold text-primary">
                    {user?.name}&apos;s Terminal
                </h1>
                <p className="text-primary/70">Interactive command interface</p>
            </div>
            <div className="mb-4 flex-1 overflow-y-auto rounded-lg border border-primary/20 bg-white/5 p-4">
                {messages.map((msg, i) => (
                    <div key={i} className="mb-3 whitespace-pre-wrap">
                        {msg.sender === "user" ? (
                            <div className="flex text-primary">
                                <span className="mr-2 font-medium">
                                    {user?.name
                                        ?.toLowerCase()
                                        ?.replace(/\s+/g, "")}{" "}
                                    $
                                </span>
                                <span>{msg.text}</span>
                            </div>
                        ) : (
                            msg.text && (
                                <div className="text-gray-300">
                                    {msg.text.split("\n").map((line, idx) => (
                                        <div key={idx}>{line}</div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                ))}
                {isLoading && (
                    <div className="text-primary/70">
                        <span className="font-medium">...</span>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            <form
                onSubmit={handleSubmit}
                className="flex flex-wrap items-center rounded-lg border border-primary/20 bg-white/5 px-3 py-2"
            >
                <span className="mr-2 font-medium text-primary">
                    {user?.name?.toLowerCase()?.replace(/\s+/g, "")}$
                </span>
                <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="flex-1 bg-transparent text-primary outline-none placeholder:text-primary/40"
                    autoFocus
                    autoComplete="off"
                    spellCheck="false"
                    placeholder={messages.length <= 1 ? 'Type "help"' : ""}
                />
            </form>
        </div>
    );
}
